/**
 * StopFormModal — création / édition d'un arrêt du référentiel.
 * `initial` : { id?, name, latitude, longitude, address, notes, active }
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ActionButton } from "@/components/ds";
import { MapPin } from "lucide-react";
import { CATEGORIES, PALETTE, ICON_KEYS, ICONS, styleOf, markerHtml } from "@/lib/stopStyles";

const FIELD = "w-full border border-zinc-900 px-3 py-2 text-sm bg-white";
const LABEL = "block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1";

function validate(f) {
  const errs = {};
  if (!f.name.trim()) errs.name = "Le nom est obligatoire.";
  const lat = parseFloat(String(f.latitude).replace(",", "."));
  const lng = parseFloat(String(f.longitude).replace(",", "."));
  if (Number.isNaN(lat) || lat < -90 || lat > 90) errs.latitude = "Latitude invalide (−90 à 90).";
  if (Number.isNaN(lng) || lng < -180 || lng > 180) errs.longitude = "Longitude invalide (−180 à 180).";
  return { errs, lat, lng };
}

export default function StopFormModal({ open, onClose, initial, onSubmit, saving }) {
  const [f, setF] = useState({ name: "", latitude: "", longitude: "", address: "", notes: "", active: true, category: "", icon_key: "", color: "" });
  const [errs, setErrs] = useState({});
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      setF({
        name: initial?.name || "",
        latitude: initial?.latitude ?? "",
        longitude: initial?.longitude ?? "",
        address: initial?.address || "",
        notes: initial?.notes || "",
        active: initial?.active !== false,
        category: initial?.category || "", icon_key: initial?.icon_key || "", color: initial?.color || "",
      });
      setErrs({});
    }
  }, [open, initial]);

  const submit = (e) => {
    e.preventDefault();
    const { errs: v, lat, lng } = validate(f);
    setErrs(v);
    if (Object.keys(v).length) return;
    onSubmit({ name: f.name.trim(), latitude: lat, longitude: lng,
      address: f.address.trim() || null, notes: f.notes.trim() || null, active: f.active,
      category: f.category || null, icon_key: f.icon_key || null, color: f.color || null });
  };

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg border border-zinc-900 rounded-none p-0" data-testid="stop-form-modal">
        <DialogHeader className="bg-zinc-900 text-white px-4 py-3">
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <MapPin size={16} className="text-[#FFC60A]" /> {isEdit ? "Modifier l'arrêt" : "Nouvel arrêt"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Référentiel central des arrêts — utilisé par les inter-arrêts et les futures tournées.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div>
            <label className={LABEL}>Nom *</label>
            <input data-testid="stop-form-name" className={FIELD} value={f.name} onChange={set("name")} autoFocus placeholder="Ex: Gare de Morges" />
            {errs.name && <p data-testid="stop-form-error-name" className="text-red-600 text-xs mt-1">{errs.name}</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Latitude *</label>
              <input data-testid="stop-form-lat" className={`${FIELD} font-mono`} value={f.latitude} onChange={set("latitude")} placeholder="46.50930" />
              {errs.latitude && <p data-testid="stop-form-error-lat" className="text-red-600 text-xs mt-1">{errs.latitude}</p>}
            </div>
            <div>
              <label className={LABEL}>Longitude *</label>
              <input data-testid="stop-form-lng" className={`${FIELD} font-mono`} value={f.longitude} onChange={set("longitude")} placeholder="6.49740" />
              {errs.longitude && <p data-testid="stop-form-error-lng" className="text-red-600 text-xs mt-1">{errs.longitude}</p>}
            </div>
          </div>
          <div>
            <label className={LABEL}>Adresse</label>
            <input data-testid="stop-form-address" className={FIELD} value={f.address} onChange={set("address")} placeholder="Optionnel" />
          </div>
          <div>
            <label className={LABEL}>Notes</label>
            <textarea data-testid="stop-form-notes" className={`${FIELD} min-h-[60px]`} value={f.notes} onChange={set("notes")} placeholder="Optionnel" />
          </div>
          <div className="border border-zinc-200 p-2 space-y-2" data-testid="stop-form-style">
            <div className="flex items-center justify-between">
              <span className={LABEL + " mb-0"}>Apparence sur la carte (facultatif)</span>
              <span data-testid="stop-form-style-preview" dangerouslySetInnerHTML={{ __html: markerHtml({ ...styleOf({ category: f.category, icon_key: f.icon_key, color: f.color }), size: 28 }) }} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={LABEL}>Catégorie</label>
                <select data-testid="stop-form-category" className={FIELD} value={f.category} onChange={set("category")}>
                  <option value="">— Autre (défaut) —</option>
                  {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={LABEL}>Couleur</label>
                <div className="flex flex-wrap gap-1" data-testid="stop-form-colors">
                  <button type="button" title="Défaut" data-testid="stop-form-color-default" onClick={() => setF((p) => ({ ...p, color: "" }))}
                    className={`w-6 h-6 border-2 text-[9px] font-mono ${f.color === "" ? "border-[#FFC60A]" : "border-zinc-300"}`}>∅</button>
                  {PALETTE.map((c) => (
                    <button key={c} type="button" title={c} data-testid={`stop-form-color-${c.slice(1)}`} onClick={() => setF((p) => ({ ...p, color: c }))}
                      style={{ background: c }} className={`w-6 h-6 border-2 ${f.color === c ? "border-[#FFC60A] scale-110" : "border-zinc-900"}`} />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label className={LABEL}>Icône</label>
              <div className="flex flex-wrap gap-1" data-testid="stop-form-icons">
                <button type="button" title="Icône de la catégorie" data-testid="stop-form-icon-default" onClick={() => setF((p) => ({ ...p, icon_key: "" }))}
                  className={`w-7 h-7 border text-[9px] font-mono ${f.icon_key === "" ? "border-[#FFC60A] bg-[#FFF7CC]" : "border-zinc-300"}`}>auto</button>
                {ICON_KEYS.map((k) => (
                  <button key={k} type="button" title={k} data-testid={`stop-form-icon-${k}`} onClick={() => setF((p) => ({ ...p, icon_key: k }))}
                    className={`w-7 h-7 border flex items-center justify-center ${f.icon_key === k ? "border-[#FFC60A] bg-[#FFF7CC]" : "border-zinc-300"}`}
                    dangerouslySetInnerHTML={{ __html: ICONS[k] }} />
                ))}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="stop-form-active" type="checkbox" checked={f.active} onChange={set("active")} />
            Arrêt actif
          </label>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <ActionButton variant="ghost" onClick={onClose} testId="stop-form-cancel">Annuler</ActionButton>
            <ActionButton variant="primary" type="submit" loading={saving} testId="stop-form-submit">
              {isEdit ? "Enregistrer" : "Créer l'arrêt"}
            </ActionButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
